import React from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'

const session = require('../../config/session')

function resolverEstadoCajero(cajero, categoriasEstados) {
  const items = []
  categoriasEstados.forEach((categoria) => {
    const tipos = []
    categoria.tipos.forEach((tipo) => {
      let estadoActual = null
      let estadoDesconocido = null
      tipo.estados.forEach((estado) => {
        if (estado.codigo_estado === 0) estadoDesconocido = estado
        cajero.estados.forEach((estadoCajero) => {
          if (estado.id === estadoCajero.estado) estadoActual = estado
        })
      })
      const resuelto = estadoActual || estadoDesconocido
      if (resuelto) {
        tipos.push({
          tipoId: tipo.id,
          descripcion: resuelto.descripcion,
          archivo: resuelto.archivo,
          codigo: resuelto.codigo_estado,
        })
      }
    })
    if (tipos.length > 0) {
      items.push({ categoriaId: categoria.id, nombre: categoria.nombre, tipos })
    }
  })
  return items
}

function ItemCajero(props) {
  const cajero = props.cajero
  const categorias = resolverEstadoCajero(cajero, props.categoriasEstados)
  const [open, setOpen] = React.useState(false)

  const totales = categorias.reduce(
    (acc, c) => {
      c.tipos.forEach((t) => {
        if (t.codigo === 0) acc.desconocidos += 1
        else acc.ok += 1
      })
      return acc
    },
    { ok: 0, desconocidos: 0 }
  )

  const sinDatos = totales.ok === 0
  const statusColor = sinDatos ? '#9e9e9e' : '#2E7D32'
  const statusLabel = sinDatos ? 'Sin datos' : 'Operativo'
  const StatusIcon = sinDatos ? HelpOutlineRoundedIcon : CheckCircleRoundedIcon

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        elevation={0}
        sx={{
          position: 'relative',
          borderRadius: '14px',
          border: '1px solid rgba(0,0,0,0.06)',
          overflow: 'hidden',
          backgroundColor: '#fff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 200ms ease, box-shadow 200ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
          },
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 4, backgroundColor: statusColor }} />

        <Box sx={{ p: 2, pl: 2.5 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#222', lineHeight: 1.2 }} noWrap>
                {cajero.alias}
              </Typography>
              <Stack direction="row" gap={1.5} sx={{ mt: 0.5 }}>
                <Tooltip title="Código">
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    <strong style={{ color: '#999' }}>Código</strong> {cajero.codigo}
                  </Typography>
                </Tooltip>
              </Stack>
              <Tooltip title={cajero.serial || ''}>
                <Typography
                  variant="caption"
                  sx={{ color: '#999', display: 'block', fontFamily: 'monospace', mt: 0.25 }}
                  noWrap
                >
                  {cajero.serial}
                </Typography>
              </Tooltip>
            </Box>

            <Chip
              icon={<StatusIcon sx={{ fontSize: 16, color: `${statusColor} !important` }} />}
              label={statusLabel}
              size="small"
              sx={{
                backgroundColor: `${statusColor}14`,
                color: statusColor,
                fontWeight: 700,
                border: `1px solid ${statusColor}30`,
              }}
            />
          </Stack>

          <Stack direction="row" gap={0.75} sx={{ mt: 1.5 }} flexWrap="wrap">
            <Chip
              size="small"
              label={`${totales.ok} reportados`}
              sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }}
            />
            {totales.desconocidos > 0 && (
              <Chip
                size="small"
                label={`${totales.desconocidos} sin reporte`}
                sx={{ backgroundColor: '#F5F5F5', color: '#666', fontWeight: 600 }}
              />
            )}
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.25 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Detalle de estados
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpen((v) => !v)}
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
              }}
              aria-label={open ? 'Ocultar detalle' : 'Mostrar detalle'}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Stack>
        </Box>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            {categorias.map((cat) => (
              <Box key={cat.categoriaId} sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 700,
                    color: '#6A9E2C',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: 0.5,
                  }}
                >
                  {cat.nombre}
                </Typography>
                <Stack gap={0.5}>
                  {cat.tipos.map((t) => (
                    <Stack
                      key={t.tipoId}
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{
                        py: 0.5,
                        px: 1,
                        borderRadius: '8px',
                        backgroundColor: t.codigo === 0 ? 'rgba(0,0,0,0.025)' : 'transparent',
                      }}
                    >
                      <img
                        alt="estado"
                        src={'/estado_cajero/' + t.archivo}
                        width={16}
                        height={16}
                        style={{ flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ color: t.codigo === 0 ? '#888' : '#333', fontSize: '0.82rem' }}>
                        {t.descripcion}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Collapse>

        <Box sx={{ mt: 'auto', p: 2, pt: 1 }}>
          {session.checkPermiso('acceder_cajeros_resumen') && (
            <Link
              to={{ pathname: '/dashboard/cajeros/resumen', state: { id_cajero: cajero.id } }}
              style={{ textDecoration: 'none' }}
            >
              <Button
                fullWidth
                variant="outlined"
                endIcon={<OpenInNewRoundedIcon />}
                sx={{
                  borderRadius: '10px',
                  borderColor: 'rgba(106,158,44,0.4)',
                  color: '#6A9E2C',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(106,158,44,0.06)',
                    borderColor: '#6A9E2C',
                  },
                }}
              >
                Ver cajero
              </Button>
            </Link>
          )}
        </Box>
      </Card>
    </Grid>
  )
}

export default ItemCajero
